// VGroveLayout不直接使用LayoutSettings，通过hooks使用
import type { SlotsToClasses } from "@heroui/react";
import clsx from "clsx";
import { useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation } from "react-router";
import styled from 'styled-components';
import type { CollapseProps } from "../base/Collapse";
import { useLayoutFeatures, useVGroveLayoutSettings } from "../hooks";
import { useTheme } from "../hooks/useTheme";
import { VGroveContext } from "../providers/VGroveContext";
import type { ContainerProps } from "../types";
import { type SidebarMenuItemsType } from "./SidebarMenus";
import { VGroveFooter } from "./VGroveFooter";
import { VGroveNavbar } from "./VGroveNavbar";
import { VGroveSidebar } from "./VGroveSidebar";
import { debug } from "../client";
import { isMobile } from 'react-device-detect';

// 🔥 修复：全局实例管理器，确保只有最新的VGroveLayout实例能设置布局状态
let currentLayoutInstance: string | null = null;

const Layout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    background-color: hsl(var(--heroui-background) / 1);
    color: hsl(var(--heroui-foreground) / 1);
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    min-width: 0;
    max-width: 100%;
`;

const Wrapper = styled.div`
    display: flex;
    height: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
`;

export interface VGroveLayoutProps extends ContainerProps {
    // 侧边栏配置
    items: SidebarMenuItemsType;
    onCollapsedChange?: CollapseProps['onCollapsedChange'];

    // Navbar 配置
    navbarProps?: Omit<React.ComponentProps<typeof VGroveNavbar>, "settings">;

    // Sidebar 配置  
    sidebarProps?: Omit<React.ComponentProps<typeof VGroveSidebar>, "settings" | "items" | "isCollapsed" | "onCollapsedChange">;

    // Footer 配置
    footerProps?: Omit<React.ComponentProps<typeof VGroveFooter>, "settings">;

    // 自定义样式类名
    classNames?: SlotsToClasses<"layout" | "wrapper" | "wrapperLayout" | "content">;
}

export const VGroveLayout: React.FC<VGroveLayoutProps> = ({
    items,
    onCollapsedChange,
    children,
    navbarProps,
    sidebarProps,
    footerProps,
    classNames: classNamesOverride,
    className
}) => {

    const layoutFeatures = useLayoutFeatures();
    const settings = useVGroveLayoutSettings();

    // 🔥 修复：添加实例ID来跟踪多个VGroveLayout实例
    const instanceId = useMemo(() => Math.random().toString(36).substring(2, 11), []);

    // 🔥 修复：注册当前实例为活跃实例
    useEffect(() => {
        currentLayoutInstance = instanceId;
        debug.log(`🔄 [VGroveLayout-${instanceId}] 注册为当前活跃实例`);

        return () => {
            if (currentLayoutInstance === instanceId) {
                currentLayoutInstance = null;
                debug.log(`🔄 [VGroveLayout-${instanceId}] 取消注册活跃实例`);
            }
        };
    }, [instanceId]);

    // 🎯 获取布局状态管理
    const vgroveContext = useContext(VGroveContext);
    if (!vgroveContext) {
        throw new Error('VGroveLayout must be used within VGroveProvider');
    }
    const { setLayoutReady } = vgroveContext;

    // 获取当前路由位置
    const location = useLocation();
    const { setTheme } = useTheme();

    const [isCollapsed, setCollapsed] = useState(settings.sidebarConfig?.defaultCollapsed ?? false);

    // 🎯 追踪布局初始化状态
    const [isLayoutInitialized, setLayoutInitialized] = useState(false);

    const lastAppliedTheme = useRef<{
        pathname: string;
        themeMode: string;
    } | null>(null);

    useEffect(() => {
        const currentThemeMode = settings.theme?.mode;
        const currentPathname = location.pathname;

        // 检查用户是否已有主题偏好
        let hasUserPreference = false;
        try {
            const savedTheme = localStorage.getItem('vgrove-theme');
            hasUserPreference = savedTheme !== null && ['light', 'dark', 'system'].includes(savedTheme);
        } catch {
            // localStorage 不可用时忽略
        }

        // 只有在用户没有偏好时，才应用 VGrove 设置的主题
        if (!hasUserPreference) {
            const shouldApplyTheme =
                !lastAppliedTheme.current ||
                lastAppliedTheme.current.themeMode !== currentThemeMode;

            if (shouldApplyTheme && currentThemeMode) {
                // 将 "auto" 映射为 "system" 以匹配我们的 ThemeMode 类型
                const mappedTheme = currentThemeMode === 'auto' ? 'system' : currentThemeMode;
                if (mappedTheme === 'light' || mappedTheme === 'dark' || mappedTheme === 'system') {
                    setTheme(mappedTheme);
                }
                lastAppliedTheme.current = {
                    pathname: currentPathname,
                    themeMode: currentThemeMode
                };
            }
        }
    }, [settings.theme?.mode, location.pathname, setTheme]);

    // 🎯 路由变化时重置滚动位置（但不重置布局状态）
    useEffect(() => {
        // 只重置滚动位置，不重置布局状态
        // 因为布局组件本身没有重新挂载，所以不需要重置布局状态
        const contentElement = document.getElementById('vgrove-content');
        if (contentElement) {
            contentElement.scrollTo(0, 0);
        }
        // 同时重置整个窗口的滚动位置（作为备用）
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // 🎯 布局完成检测 - 只在组件挂载时执行一次
    useEffect(() => {
        debug.log(`🏗️ [VGroveLayout-${instanceId}] 布局组件已挂载，设置为loading状态`, {
            pathname: location.pathname,
            isLayoutInitialized,
            timestamp: new Date().toISOString()
        });

        // 🔥 修复：先设置为loading状态，表示VGroveLayout正在初始化
        setLayoutReady(false);

        // 🔥 修复：立即同步设置布局为ready，避免任何异步延迟导致的问题
        // 只有当前活跃实例才能设置布局状态
        if (currentLayoutInstance === instanceId) {
            debug.log(`🎉 [VGroveLayout-${instanceId}] 布局完成！立即设置 isLayoutReady = true`, {
                pathname: location.pathname,
                timestamp: new Date().toISOString()
            });
            setLayoutInitialized(true);
            setLayoutReady(true);
        } else {
            debug.log(`⚠️ [VGroveLayout-${instanceId}] 不是活跃实例，跳过布局完成设置`, {
                currentActive: currentLayoutInstance,
                pathname: location.pathname
            });
        }

        return () => {
            debug.log(`🧹 [VGroveLayout-${instanceId}] 清理布局完成检测`);
        };
    }, []); // 空依赖数组，只在组件挂载时执行一次

    const handleCollapseChange: CollapseProps['onCollapsedChange'] = (isCollapsed) => {
        setCollapsed(isCollapsed);
        onCollapsedChange?.(isCollapsed);
    }

    const contentLayoutStyles = useMemo(() => {
        const cssProperties: CSSProperties = {
            flex: '1 1 0%',
            width: "100%",
            maxWidth: "100vw",
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
        };

        const actualLayoutFeatures = {
            hasHeader: layoutFeatures.hasHeader,
            hasSidebar: layoutFeatures.hasSidebar,
            hasFooter: layoutFeatures.hasFooter,
        };
        if (!isMobile) {
            if (actualLayoutFeatures.hasSidebar && !isCollapsed) {
                cssProperties.paddingInlineStart = settings.sidebarConfig?.width;
            } else if (actualLayoutFeatures.hasSidebar) {
                cssProperties.paddingInlineStart = '64px';
            }
        }

        return cssProperties;
    }, [isCollapsed, layoutFeatures, settings.sidebarConfig?.width]);

    // 构建实际的布局功能控制
    const actualLayoutFeatures = useMemo(() => ({
        hasHeader: layoutFeatures.hasHeader,
        hasSidebar: layoutFeatures.hasSidebar,
        hasFooter: layoutFeatures.hasFooter,
    }), [layoutFeatures]);

    return (
        <Layout
            id="vgrove-layout__0"
            className={clsx("min-w-0 max-w-full overflow-hidden", classNamesOverride?.layout, className)}
        >
            {actualLayoutFeatures.hasHeader && (
                <VGroveNavbar
                    className={clsx(
                        "transition-all duration-300 ease-in-out",
                        "min-w-0 max-w-full",
                        navbarProps?.className
                    )}
                    settings={settings}
                    {...navbarProps}
                />
            )}
            <Layout id="vgrove-layout__1" className="overflow-hidden min-w-0 max-w-full">
                {actualLayoutFeatures.hasSidebar && (
                    <VGroveSidebar
                        settings={settings}
                        items={items}
                        isCollapsed={isCollapsed}
                        onCollapsedChange={handleCollapseChange}
                        {...sidebarProps}
                    />
                )}
                <Wrapper
                    id="vgrove-layout-wrapper"
                    className={clsx("min-w-0 max-w-full overflow-hidden", classNamesOverride?.wrapper)}
                >
                    <Layout
                        id="vgrove-layout__3"
                        className={clsx(
                            "transition-[padding-inline-start]",
                            "min-w-0 max-w-full overflow-hidden",
                            {
                                "md:flex": settings.responsive?.hideSidebarOnMobile,
                            },
                            classNamesOverride?.wrapperLayout
                        )}
                        style={contentLayoutStyles}
                    >
                        <Content
                            id="vgrove-content"
                            className={clsx("min-w-0 max-w-full", classNamesOverride?.content)}
                        >
                            {children}
                        </Content>
                        {actualLayoutFeatures.hasFooter && <VGroveFooter settings={settings} {...footerProps} />}
                    </Layout>
                </Wrapper>
            </Layout>
        </Layout>
    )
}